import { useState, useEffect, useMemo, Fragment } from 'react'
import { useParams, Link, useNavigate } from 'react-router-dom'
import { useSupabase } from '@/hooks/useSupabase'
import { useApi } from '@/hooks/useApi'
import type { Proyecto, Tarea, Cliente, Servicio, Cotizacion, Factura, TareaEvidencia, ConfigTarifaCliente, EstadoProyecto, ProyectoCotizacion, Conversacion } from '@/lib/types'
import { formatDate, formatCurrency, r2, exportToExcel } from '@/lib/utils'
import KanbanBoard from '@/components/proyectos/KanbanBoard'
import IntegracionesProyecto from '@/components/proyectos/IntegracionesProyecto'
import TareaModal from '@/components/proyectos/TareaModal'
import ProyectoModal from '@/components/proyectos/ProyectoModal'
import FacturarTareasModal from '@/components/proyectos/FacturarTareasModal'
import CajaProyecto from '@/components/proyectos/CajaProyecto'
import ImportarTareasModal from '@/components/proyectos/ImportarTareasModal'
import ReportesProyecto from '@/components/proyectos/ReportesProyecto'
import PanelCorreos from '@/components/correos/PanelCorreos'
import HitosProyecto from '@/components/proyectos/HitosProyecto'
import ReunionesProyecto from '@/components/proyectos/ReunionesProyecto'
import CotizacionVisorModal from '@/components/proyectos/CotizacionVisorModal'
import { MultiSelect } from '@/components/ui'
import {
  IconEdit, IconCircleFilled, IconUser, IconPackage, IconFileText,
  IconCheckCircle, IconKanban, IconList, IconBarChart,
  IconPlug,
  IconChevronRight, IconChevronDown,
  IconAlertTriangle, IconClock, IconRefresh, IconBan, IconFilePlus,
  IconArrowDown, IconArrowRight, IconArrowUp, IconAlertCircle,
  IconMail, IconXCircle, IconPlus, IconDollar, IconSearch, IconMoreVertical,
  IconCalendar, IconExternalLink, IconTrash, IconWhatsApp,
} from '@/components/icons/Icons'

// Excel export icon (inline SVG — no new component needed)
const IconDownload = ({ size = 16 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
    <polyline points="7 10 12 15 17 10"/>
    <line x1="12" y1="15" x2="12" y2="3"/>
  </svg>
)

const ESTADO_LABEL: Record<EstadoProyecto, { label: string; icon: React.ReactNode; className: string }> = {
  activo: { label: 'Activo', icon: <IconCircleFilled size={8} style={{ color: 'var(--success)' }} />, className: 'badge-activo' },
  pausado: { label: 'Pausado', icon: <IconCircleFilled size={8} style={{ color: 'var(--warning)' }} />, className: 'badge-pausado' },
  completado: { label: 'Completado', icon: <IconCircleFilled size={8} style={{ color: 'var(--info)' }} />, className: 'badge-completado' },
  cancelado: { label: 'Cancelado', icon: <IconCircleFilled size={8} style={{ color: 'var(--danger)' }} />, className: 'badge-cancelado' },
}

interface DetalleProyecto {
  proyecto: Proyecto
  cliente: Cliente | null
  servicio: Servicio | null
  tareas: Tarea[]
  cotizaciones: Cotizacion[]
  proyecto_cotizaciones: ProyectoCotizacion[]
  facturas: Factura[]
  evidencias: TareaEvidencia[]
  tarifa_cliente: ConfigTarifaCliente | null
}

/*
  Lo ultimo que se vio de cada proyecto, en memoria de la pestana.

  Volver de la ficha de una cotizacion al proyecto remontaba el componente y
  dejaba "Cargando proyecto..." en pantalla mientras se repetia la consulta
  entera. Con esto se pinta lo que ya estaba y se refresca por detras.
  No persiste: al recargar la pagina se pide de nuevo, que es lo correcto.
*/
const cacheDetalle = new Map<number, DetalleProyecto>()

type ViewMode = 'info' | 'kanban' | 'list' | 'caja' | 'reportes' | 'correos' | 'integraciones'

export default function VerProyecto() {
  const { id, tab } = useParams<{ id: string; tab?: string }>()
  const navigate = useNavigate()
  const db = useSupabase()
  const api = useApi()

  const [proyecto, setProyecto] = useState<Proyecto | null>(null)
  const [tareas, setTareas] = useState<Tarea[]>([])
  const [cliente, setCliente] = useState<Cliente | null>(null)
  const [servicio, setServicio] = useState<Servicio | null>(null)
  const [cotizacionesVinculadas, setCotizacionesVinculadas] = useState<Cotizacion[]>([])
  // Catalogos completos: solo hacen falta para el modal de edicion y para el
  // desplegable de vincular cotizacion, asi que se piden cuando se abren.
  const [clientes, setClientes] = useState<Cliente[]>([])
  const [servicios, setServicios] = useState<Servicio[]>([])
  const [cotizaciones, setCotizaciones] = useState<Cotizacion[]>([])
  const [catalogosListos, setCatalogosListos] = useState(false)
  const [facturas, setFacturas] = useState<Factura[]>([])
  const [evidencias, setEvidencias] = useState<TareaEvidencia[]>([])
  const [tarifaCliente, setTarifaCliente] = useState<ConfigTarifaCliente | null>(null)
  const [proyectoCotizaciones, setProyectoCotizaciones] = useState<ProyectoCotizacion[]>([])
  const [chatsVinculados, setChatsVinculados] = useState<Conversacion[]>([])
  const [loading, setLoading] = useState(true)
  const [menuAbierto, setMenuAbierto] = useState(false)
  const [visorCotizacion, setVisorCotizacion] = useState<{ id: number; numero?: number | string } | null>(null)
  const [showAddCotizacion, setShowAddCotizacion] = useState(false)
  const [addingCotizacion, setAddingCotizacion] = useState(false)

  const viewMode: ViewMode = (tab === 'kanban' || tab === 'list' || tab === 'caja' || tab === 'reportes' || tab === 'correos' || tab === 'integraciones') ? tab : 'info'
  const setViewMode = (mode: ViewMode) => {
    if (mode === 'info') navigate(`/proyectos/${id}`, { replace: true })
    else navigate(`/proyectos/${id}/${mode}`, { replace: true })
  }
  const [showTareaModal, setShowTareaModal] = useState(false)
  const [editTarea, setEditTarea] = useState<Tarea | null>(null)
  const [showProyectoModal, setShowProyectoModal] = useState(false)
  const [showFacturarModal, setShowFacturarModal] = useState(false)
  const [showImportModal, setShowImportModal] = useState(false)
  const [preSelectedTaskIds, setPreSelectedTaskIds] = useState<number[]>([])
  const [listSelected, setListSelected] = useState<Set<number>>(new Set())
  const [bulkSaving, setBulkSaving] = useState(false)
  // Listas, no strings: filtrar un tablero casi nunca es elegir una sola cosa.
  // Lista vacia = sin filtro.
  const [filterEstado, setFilterEstado] = useState<string[]>([])
  const [filterEtiqueta, setFilterEtiqueta] = useState<string[]>([])
  const [filterTipo, setFilterTipo] = useState<string[]>([])
  const [searchQuery, setSearchQuery] = useState('')
  const [listPage, setListPage] = useState(0)
  const PAGE_SIZE = 25

  /* Agrupacion por hito.

     Un "hito" no es un tipo nuevo: es una tarea que tiene hijas colgando de
     `parent_id`. Esa jerarquia existia hace meses y ninguna pantalla la leia,
     asi que FUTURA mostraba 200 filas planas con el bloque padre y sus hijas
     mezclados, sin forma de saber cual pertenecia a cual. */
  const [agruparHitos, setAgruparHitos] = useState(true)
  const [hitosAbiertos, setHitosAbiertos] = useState<Set<number>>(new Set())
  const [hitoDetalle, setHitoDetalle] = useState<number | null>(null)

  /* Una sola ida al servidor con todo lo del proyecto. Antes se pedian las
     tablas ENTERAS de tareas, facturas, cotizaciones, clientes y servicios y
     se filtraba en el navegador: varios MB por visita y el "Cargando" de
     cada vez. */
  async function loadData(isInitial = false) {
    if (!id) return
    const pid = Number(id)

    // Si ya se vio, se pinta y se refresca por detras: nada de pantalla vacia.
    const cacheado = cacheDetalle.get(pid)
    if (isInitial && cacheado) {
      aplicarDetalle(cacheado)
      setLoading(false)
    } else if (isInitial) {
      setLoading(true)
    }

    try {
      const d = await api.get<DetalleProyecto>(`/api/proyectos/${pid}/detalle`)
      cacheDetalle.set(pid, d)
      aplicarDetalle(d)
    } catch (err) {
      console.error('Error loading project:', err)
    } finally {
      setLoading(false)
    }

    // Los chats vinculados viven en otro dominio y pueden fallar sin que la
    // ficha deje de abrir.
    try {
      setChatsVinculados(await api.get<Conversacion[]>(`/api/chat/conversaciones?proyecto_id=${pid}`))
    } catch { /* ok */ }
  }

  function aplicarDetalle(d: DetalleProyecto) {
    setProyecto(d.proyecto)
    setCliente(d.cliente)
    setServicio(d.servicio)
    setTareas(d.tareas ?? [])
    setCotizacionesVinculadas(d.cotizaciones ?? [])
    setProyectoCotizaciones(d.proyecto_cotizaciones ?? [])
    setFacturas(d.facturas ?? [])
    setEvidencias(d.evidencias ?? [])
    setTarifaCliente(d.tarifa_cliente ?? null)
  }

  /* Los catalogos completos solo hacen falta para editar el proyecto o para
     vincular una cotizacion. Se piden al abrir eso, no al entrar. */
  async function cargarCatalogos() {
    if (catalogosListos) return
    try {
      const [c, sv, cot] = await Promise.all([
        db.getAll<Cliente>('clientes'),
        db.getAll<Servicio>('servicios'),
        db.getAll<Cotizacion>('cotizaciones'),
      ])
      setClientes(c)
      setServicios(sv)
      setCotizaciones(cot)
      setCatalogosListos(true)
    } catch (err) {
      console.error('Error cargando catalogos:', err)
    }
  }

  useEffect(() => { loadData(true) }, [id]) // eslint-disable-line react-hooks/exhaustive-deps

  const clienteName = cliente?.empresa ?? 'Sin cliente'

  const servicioName = servicio?.nombre ?? null

  // Available cotizaciones not yet linked to this project
  const availableCotizaciones = useMemo(() => {
    const linkedIds = new Set(proyectoCotizaciones.map(pc => pc.cotizacion_id))
    return cotizaciones.filter(c => !linkedIds.has(c.id))
  }, [cotizaciones, proyectoCotizaciones])


  async function handleAddCotizacion(cotizacionId: number) {
    if (!id || addingCotizacion) return
    setAddingCotizacion(true)
    try {
      await api.post(`/api/proyectos/${id}/cotizaciones`, { cotizacion_id: cotizacionId })
      await loadData()
      setShowAddCotizacion(false)
    } catch (err) {
      console.error('Error linking cotizacion:', err)
    } finally {
      setAddingCotizacion(false)
    }
  }

  async function handleRemoveCotizacion(cotizacionId: number) {
    if (!id || !confirm('¿Desvincular esta cotización del proyecto?')) return
    try {
      await api.del(`/api/proyectos/${id}/cotizaciones/${cotizacionId}`)
      await loadData()
    } catch (err) {
      console.error('Error unlinking cotizacion:', err)
    }
  }

  const stats = useMemo(() => {
    const total = tareas.length
    const done = tareas.filter(t => t.estado === 'completada').length
    const progress = total > 0 ? Math.round((done / total) * 100) : 0
    const overdue = tareas.filter(t =>
      t.fecha_limite && new Date(t.fecha_limite + 'T23:59:59') < new Date() && t.estado !== 'completada'
    ).length
    // Pricing stats
    const montoTotal = r2(tareas.filter(t => t.tipo_cobro !== 'sin_costo').reduce((s, t) => s + (t.monto || 0), 0))
    const horasTotal = r2(tareas.filter(t => t.tipo_cobro === 'por_horas').reduce((s, t) => s + (t.horas_estimadas || 0), 0))
    const montoAprobado = r2(tareas.filter(t => t.tipo_cobro !== 'sin_costo' && t.estado_aprobacion === 'aprobada').reduce((s, t) => s + (t.monto || 0), 0))
    const facturables = tareas.filter(t => t.estado_aprobacion === 'aprobada' && !t.factura_id).length
    return { total, done, progress, overdue, montoTotal, horasTotal, montoAprobado, facturables }
  }, [tareas])

  // Umbral alert
  const umbralAlert = useMemo(() => {
    if (!tarifaCliente || tarifaCliente.umbral_horas_mes <= 0) return null
    const horasUsadas = tareas.filter(t => t.tipo_cobro === 'por_horas').reduce((s, t) => s + (t.horas_estimadas || 0), 0)
    const pct = Math.round((horasUsadas / tarifaCliente.umbral_horas_mes) * 100)
    if (pct >= 80) return { horasUsadas: r2(horasUsadas), umbral: tarifaCliente.umbral_horas_mes, pct, critical: pct >= 100 }
    return null
  }, [tareas, tarifaCliente])

  // List view sorted by priority then by fecha_limite
  const sortedTareas = useMemo(() => {
    const prioOrder: Record<string, number> = { urgente: 0, alta: 1, media: 2, baja: 3 }
    return [...tareas].sort((a, b) => {
      const pa = prioOrder[a.prioridad] ?? 2
      const pb = prioOrder[b.prioridad] ?? 2
      if (pa !== pb) return pa - pb
      return (a.fecha_limite ?? '').localeCompare(b.fecha_limite ?? '')
    })
  }, [tareas])

  // All unique etiquetas for filter dropdown
  const allEtiquetas = useMemo(() => {
    const set = new Set<string>()
    tareas.forEach(t => t.etiquetas?.forEach(e => set.add(e)))
    return Array.from(set).sort()
  }, [tareas])

  // Apply filters + search
  const filteredTareas = useMemo(() => {
    const q = searchQuery.toLowerCase().trim()
    return sortedTareas.filter(t => {
      if (q && !t.titulo.toLowerCase().includes(q)) return false
      // Dentro de un filtro, O; entre filtros, Y. Marcar "pendiente" y
      // "en progreso" trae las dos, no la interseccion (que seria vacia).
      if (filterEstado.length && !filterEstado.includes(t.estado)) return false
      if (filterEtiqueta.length && !filterEtiqueta.some(e => t.etiquetas?.includes(e))) return false
      if (filterTipo.length && !filterTipo.includes(t.tipo)) return false
      return true
    })
  }, [sortedTareas, filterEstado, filterEtiqueta, filterTipo, searchQuery])

  // Tareas filtered by search only (for Kanban/Caja which have their own internal filtering)
  const searchedTareas = useMemo(() => {
    if (!searchQuery.trim()) return tareas
    const q = searchQuery.toLowerCase().trim()
    return tareas.filter(t => t.titulo.toLowerCase().includes(q))
  }, [tareas, searchQuery])

  /* Los grupos de la lista: un hito por cada tarea con hijas, mas el cajon
     de las sueltas.

     Se arma sobre `filteredTareas` y no sobre `tareas` para que filtrar por
     estado o etiqueta siga funcionando: un hito cuyas hijas quedaron todas
     fuera del filtro no se muestra.

     El PADRE se saca de las filas normales — pasa a ser el encabezado — y por
     eso se lo busca en `tareas` y no en las filtradas: si el filtro excluye al
     padre (por ejemplo, filtrando "pendiente" cuando el padre esta en
     progreso) sus hijas igual tienen que saber de quien cuelgan. */
  const gruposHito = useMemo(() => {
    const porId = new Map(tareas.map(t => [t.id, t]))
    const conHijas = new Set(tareas.map(t => t.parent_id).filter(Boolean) as number[])

    const grupos = new Map<number, { hito: Tarea; hijas: Tarea[] }>()
    const sueltas: Tarea[] = []

    for (const t of filteredTareas) {
      if (conHijas.has(t.id)) continue          // es un hito: va de encabezado
      const padre = t.parent_id ? porId.get(t.parent_id) : null
      if (!padre) { sueltas.push(t); continue }
      const g = grupos.get(padre.id) ?? { hito: padre, hijas: [] }
      g.hijas.push(t)
      grupos.set(padre.id, g)
    }

    // El hito con mas trabajo abierto primero: es donde esta la atencion.
    const ordenados = [...grupos.values()].sort((a, b) =>
      b.hijas.filter(h => h.estado !== 'completada').length -
      a.hijas.filter(h => h.estado !== 'completada').length,
    )
    return { grupos: ordenados, sueltas }
  }, [filteredTareas, tareas])

  const hayHitos = gruposHito.grupos.length > 0

  /* Arrancan colapsados menos el que tenga algo en progreso: con 8 hitos y 200
     tareas, abrirlos todos devuelve el problema que esto viene a resolver. */
  useEffect(() => {
    if (!hayHitos) return
    setHitosAbiertos(prev => {
      if (prev.size) return prev
      const enMarcha = gruposHito.grupos
        .filter(g => g.hijas.some(h => h.estado === 'en_progreso'))
        .map(g => g.hito.id)
      return new Set(enMarcha)
    })
  }, [hayHitos, gruposHito.grupos])

  function toggleHito(id: number) {
    setHitosAbiertos(prev => {
      const s = new Set(prev)
      s.has(id) ? s.delete(id) : s.add(id)
      return s
    })
  }

  const totalPages = Math.max(1, Math.ceil(filteredTareas.length / PAGE_SIZE))
  const paginatedTareas = useMemo(() => {
    const start = listPage * PAGE_SIZE
    return filteredTareas.slice(start, start + PAGE_SIZE)
  }, [filteredTareas, listPage])

  /* Una fila de la tabla. Se extrajo del map para poder dibujarla en los
     dos modos: suelta cuando no se agrupa, y debajo de su hito cuando si. */
  function filaTarea(t: Tarea) {
    const isOverdue = t.fecha_limite && new Date(t.fecha_limite + 'T23:59:59') < new Date() && t.estado !== 'completada'
    const hasCost = t.tipo_cobro !== 'sin_costo' && t.monto > 0
    const isChecked = listSelected.has(t.id)
    return (
      <tr key={t.id} style={{ background: isChecked ? 'color-mix(in srgb, var(--primary-light) 6%, transparent)' : undefined }}>
        <td>
          <input
            type="checkbox"
            checked={isChecked}
            onChange={() => toggleListSelect(t.id)}
          />
        </td>
        <td>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            {t.tipo === 'bug' && (
              <span style={{ fontSize: '0.68rem', fontWeight: 700, padding: '1px 6px', borderRadius: 4, background: 'color-mix(in srgb, var(--danger) 12%, transparent)', color: 'var(--danger)', flexShrink: 0 }}>Bug</span>
            )}
            {t.tipo === 'mejora' && (
              <span style={{ fontSize: '0.68rem', fontWeight: 700, padding: '1px 6px', borderRadius: 4, background: 'color-mix(in srgb, var(--success) 12%, transparent)', color: 'var(--success)', flexShrink: 0 }}>Mejora</span>
            )}
            <Link
              to={`/proyectos/${id}/tareas/${t.id}`}
              style={{ fontWeight: 500, color: 'inherit', textDecoration: 'none' }}
            >
              {t.titulo}
            </Link>
          </div>
        </td>
        <td>
          <span className={`badge badge-tarea-${t.estado}`} style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
            {t.estado === 'pendiente' && <IconClock size={12} />}
            {t.estado === 'en_progreso' && <IconRefresh size={12} />}
            {t.estado === 'completada' && <IconCheckCircle size={12} />}
            {t.estado === 'bloqueada' && <IconBan size={12} />}
            {t.estado === 'cancelada' && <IconXCircle size={12} />}
            {t.estado.replace('_', ' ')}
          </span>
        </td>
        <td>
          <span className={`badge badge-prioridad-${t.prioridad}`} style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
            {t.prioridad === 'baja' && <IconArrowDown size={12} />}
            {t.prioridad === 'media' && <IconArrowRight size={12} />}
            {t.prioridad === 'alta' && <IconArrowUp size={12} />}
            {t.prioridad === 'urgente' && <IconAlertCircle size={12} />}
            {t.prioridad}
          </span>
        </td>
        <td>
          {hasCost ? (
            <span style={{ fontWeight: 600, color: 'var(--accent-primary)', fontSize: '0.85rem' }}>
              {t.tipo_cobro === 'por_horas' ? `${t.horas_estimadas}h · ` : ''}
              {formatCurrency(t.monto, 'USD')}
            </span>
          ) : (
            <span style={{ color: 'var(--text-muted)', fontSize: '0.85rem' }}>—</span>
          )}
        </td>
        <td>
          {hasCost ? (
            <span style={{
              fontSize: '0.78rem', padding: '2px 8px', borderRadius: 8,
              background: t.estado_aprobacion === 'aprobada' ? 'color-mix(in srgb, var(--success) 15%, transparent)' :
                t.estado_aprobacion === 'rechazada' ? 'color-mix(in srgb, var(--danger) 15%, transparent)' :
                  t.estado_aprobacion === 'enviada' ? 'rgba(52,152,219,0.15)' : 'transparent',
              color: t.estado_aprobacion === 'aprobada' ? 'var(--success)' :
                t.estado_aprobacion === 'rechazada' ? 'var(--danger)' :
                  t.estado_aprobacion === 'enviada' ? '#3498db' : 'var(--text-muted)',
            }}>
              {t.estado_aprobacion === 'pendiente' && <IconClock size={11} />}
              {t.estado_aprobacion === 'enviada' && <IconMail size={11} />}
              {t.estado_aprobacion === 'aprobada' && <IconCheckCircle size={11} />}
              {t.estado_aprobacion === 'rechazada' && <IconXCircle size={11} />}
              {' '}{t.estado_aprobacion}
            </span>
          ) : '—'}
        </td>
        <td style={{ fontSize: '0.82rem', color: 'var(--text-muted)' }}>
          {t.created_at ? formatDate(t.created_at.split('T')[0]) : '—'}
        </td>
        <td style={{ color: isOverdue ? 'var(--danger)' : undefined }}>
          {t.fecha_limite ? formatDate(t.fecha_limite) : '—'}
          {isOverdue && <> <IconAlertTriangle size={12} /></>}
        </td>
        <td>
          {t.etiquetas.slice(0, 3).map(tag => (
            <span key={tag} className="tag-chip small" style={{ marginRight: 4 }}>{tag}</span>
          ))}
        </td>
        <td>
          <button className="btn btn-sm btn-secondary" onClick={() => openEditTarea(t)} style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
            <IconEdit size={13} /> Editar
          </button>
        </td>
      </tr>
    )
  }

  function openCreateTarea() {
    setEditTarea(null)
    setShowTareaModal(true)
  }

  function openEditTarea(t: Tarea) {
    setEditTarea(t)
    setShowTareaModal(true)
  }

  async function handleExportTareas() {
    const nombreProyecto = proyecto?.nombre ?? 'Proyecto'
    const rows = filteredTareas.map(t => ({
      'Título': t.titulo,
      'Estado': t.estado.replace('_', ' '),
      'Prioridad': t.prioridad,
      'Tipo': t.tipo ?? 'tarea',
      'Hrs Estimadas': t.horas_estimadas ?? 0,
      'Hrs Reales': t.horas_reales ?? 0,
      'Tipo Cobro': t.tipo_cobro ?? 'sin_costo',
      'Monto (USD)': t.monto ?? 0,
      'Aprobación': t.estado_aprobacion ?? 'pendiente',
      'Fecha Límite': t.fecha_limite ?? '',
      'Etiquetas': (t.etiquetas ?? []).join(', '),
      'Notas': t.notas ?? '',
    }))
    await exportToExcel(rows, `Tareas - ${nombreProyecto}`, 'Tareas')
  }

  async function handleQuickApprove(tareaId: number) {
    await db.update('tareas', { id: tareaId, estado_aprobacion: 'aprobada' })
    await loadData()
  }

  // Bulk edit helpers
  function toggleListSelect(id: number) {
    setListSelected(prev => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  function toggleListSelectAll() {
    if (listSelected.size === filteredTareas.length) {
      setListSelected(new Set())
    } else {
      setListSelected(new Set(filteredTareas.map(t => t.id)))
    }
  }

  async function bulkUpdate(field: string, value: string | null) {
    if (listSelected.size === 0 || bulkSaving) return
    setBulkSaving(true)
    try {
      const updates = Array.from(listSelected).map(id => {
        const payload: Record<string, unknown> = { id }
        payload[field] = value
        // If changing to completada, set fecha_completada
        if (field === 'estado' && value === 'completada') {
          payload.fecha_completada = new Date().toISOString()
        }
        return db.update('tareas', payload)
      })
      await Promise.all(updates)
      await loadData()
      setListSelected(new Set())
    } catch (err) {
      console.error('Bulk update error:', err)
    } finally {
      setBulkSaving(false)
    }
  }

  /* Una sola barra de filtros para Lista y Kanban. Tenerla solo en Lista
     obligaba a cambiar de pestana para acotar el tablero, y duplicarla las
     dejaria separarse con el tiempo. */
  const barraFiltros = (
                <div style={{
          display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12,
          padding: '8px 0', flexWrap: 'wrap',
        }}>
          <span style={{ fontSize: '0.82rem', color: 'var(--text-muted)', fontWeight: 600 }}>Filtros:</span>
          <MultiSelect
            placeholder="Todos los estados"
            minWidth={150}
            options={[
              { value: 'pendiente', label: 'Pendiente' },
              { value: 'en_progreso', label: 'En Progreso' },
              { value: 'completada', label: 'Completada' },
              { value: 'bloqueada', label: 'Bloqueada' },
              { value: 'cancelada', label: 'Cancelada' },
            ]}
            value={filterEstado}
            onChange={v => { setFilterEstado(v); setListSelected(new Set()); setListPage(0) }}
          />
          <MultiSelect
            placeholder="Todos los tipos"
            minWidth={130}
            options={[
              { value: 'tarea', label: 'Tarea' },
              { value: 'bug', label: 'Bug' },
              { value: 'mejora', label: 'Mejora' },
            ]}
            value={filterTipo}
            onChange={v => { setFilterTipo(v); setListSelected(new Set()); setListPage(0) }}
          />
          <MultiSelect
            placeholder="Todas las etiquetas"
            minWidth={160}
            options={allEtiquetas.map(tag => ({ value: tag, label: tag }))}
            value={filterEtiqueta}
            onChange={v => { setFilterEtiqueta(v); setListSelected(new Set()); setListPage(0) }}
          />
          {/* El interruptor solo aparece si el proyecto tiene hitos: en uno sin
              jerarquia seria un control que no hace nada. */}
          {hayHitos && (
            <button
              className={`tag-chip ${agruparHitos ? 'active' : ''}`}
              onClick={() => { setAgruparHitos(v => !v); setListPage(0) }}
              title={agruparHitos ? 'Ver todas las tareas en una lista plana' : 'Agrupar las tareas por su hito'}
            >
              Agrupar por hito
            </button>
          )}
          {(filterEstado.length > 0 || filterEtiqueta.length > 0 || filterTipo.length > 0) && (
            <button
              className="btn btn-sm btn-secondary"
              onClick={() => { setFilterEstado([]); setFilterEtiqueta([]); setFilterTipo([]); setListSelected(new Set()); setListPage(0) }}
              style={{ fontSize: '0.78rem' }}
            >
              Limpiar filtros
            </button>
          )}
          <span style={{ fontSize: '0.78rem', color: 'var(--text-muted)', marginLeft: 'auto' }}>
            {filteredTareas.length} de {tareas.length} tareas
          </span>
        </div>
  )

  if (loading) {
    return <div className="page-enter"><div className="empty-state"><p>Cargando proyecto...</p></div></div>
  }

  if (!proyecto) {
    return (
      <div className="page-enter">
        <div className="empty-state">
          <h3>Proyecto no encontrado</h3>
          <Link to="/proyectos" className="btn btn-primary">← Volver a proyectos</Link>
        </div>
      </div>
    )
  }

  const estadoInfo = ESTADO_LABEL[proyecto.estado]

  return (
    <div className="page-enter" id="page-ver-proyecto">
      {/* Encabezado compacto. Antes esto eran tres bloques apilados —titulo,
          cuatro botones grandes y una barra de estadisticas— que se comian un
          tercio del alto util y seguian ahi al cambiar de pestana. Ahora: una
          linea de breadcrumb con las acciones, y una linea de datos. */}
      <div className="proyecto-head">
        <nav className="proyecto-head__crumbs" aria-label="Ruta de navegacion">
          <Link to="/proyectos" className="proyecto-head__crumb-link">Proyectos</Link>
          <span className="proyecto-head__sep">/</span>
          <span className="proyecto-head__actual" title={proyecto.nombre}>{proyecto.nombre}</span>
        </nav>
        <span className={`badge ${estadoInfo.className}`} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          {estadoInfo.icon} {estadoInfo.label}
        </span>

        <div className="proyecto-head__acciones">
          {stats.facturables > 0 && (
            <button
              className="btn btn-secondary btn-sm"
              onClick={() => { setPreSelectedTaskIds([]); setShowFacturarModal(true) }}
              style={{ display: 'flex', alignItems: 'center', gap: 4 }}
            >
              <IconFilePlus size={14} /> Facturar ({stats.facturables})
            </button>
          )}
          <button className="btn btn-primary btn-sm" onClick={openCreateTarea} id="btn-nueva-tarea" style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <IconPlus size={14} /> Nueva Tarea
          </button>
          <div className="proyecto-menu">
            <button
              className="btn btn-secondary btn-sm"
              onClick={() => setMenuAbierto(v => !v)}
              title="Mas acciones"
              aria-haspopup="menu"
              aria-expanded={menuAbierto}
            >
              <IconMoreVertical size={16} />
            </button>
            {menuAbierto && (
              <>
                {/* Capa transparente: cerrar al tocar fuera sin montar un
                    listener global en document. */}
                <div
                  style={{ position: 'fixed', inset: 0, zIndex: 39 }}
                  onClick={() => setMenuAbierto(false)}
                />
                <div className="proyecto-menu__panel" role="menu">
                  <button className="proyecto-menu__item" onClick={() => { setMenuAbierto(false); cargarCatalogos(); setShowProyectoModal(true) }}>
                    <IconEdit size={14} /> Editar proyecto
                  </button>
                  {tareas.length > 0 && (
                    <button className="proyecto-menu__item" onClick={() => { setMenuAbierto(false); handleExportTareas() }} id="btn-export-tareas">
                      <IconDownload size={14} /> Exportar tareas a Excel
                    </button>
                  )}
                  <button className="proyecto-menu__item" onClick={() => { setMenuAbierto(false); setShowImportModal(true) }} id="btn-import-tareas">
                    <IconFilePlus size={14} /> Importar tareas desde Excel
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Una sola linea con lo que antes eran ocho tarjetas de estadistica. */}
      <div className="proyecto-head__meta">
        <span className="proyecto-head__meta-item"><IconUser size={12} /> {clienteName}</span>
        {servicioName && <span className="proyecto-head__meta-item"><IconPackage size={12} /> {servicioName}</span>}
        {proyectoCotizaciones.length > 0 && (
          <span className="proyecto-head__meta-item">
            <IconFileText size={12} /> {proyectoCotizaciones.length} cotizacion{proyectoCotizaciones.length > 1 ? 'es' : ''}
          </span>
        )}
        <span className="proyecto-head__sep">|</span>
        <span className="proyecto-head__meta-item">{stats.done}/{stats.total} tareas</span>
        <span className="proyecto-head__barra"><span style={{ width: `${stats.progress}%` }} /></span>
        <span className="proyecto-head__meta-item">{stats.progress}%</span>
        {stats.overdue > 0 && (
          <span className="proyecto-head__meta-item" style={{ color: 'var(--danger)', fontWeight: 600 }}>
            <IconAlertTriangle size={12} /> {stats.overdue} vencidas
          </span>
        )}
        {stats.horasTotal > 0 && <span className="proyecto-head__meta-item">{stats.horasTotal} h estimadas</span>}
        {proyecto.presupuesto > 0 && (
          <span className="proyecto-head__meta-item">{formatCurrency(proyecto.presupuesto, proyecto.moneda)}</span>
        )}
        {proyecto.fecha_inicio && <span className="proyecto-head__meta-item"><IconCalendar size={12} /> {formatDate(proyecto.fecha_inicio)}</span>}
        {proyecto.fecha_fin_estimada && <span className="proyecto-head__meta-item">&rarr; {formatDate(proyecto.fecha_fin_estimada)}</span>}
      </div>

      {/* Umbral alert */}
      {umbralAlert && (
        <div style={{
          padding: '10px 16px', borderRadius: 8, marginBottom: 16,
          background: umbralAlert.critical ? 'color-mix(in srgb, var(--danger) 12%, transparent)' : 'rgba(243,156,18,0.12)',
          border: `1px solid ${umbralAlert.critical ? 'color-mix(in srgb, var(--danger) 40%, transparent)' : 'rgba(243,156,18,0.4)'}`,
          fontSize: '0.85rem', display: 'flex', alignItems: 'center', gap: 8,
        }}>
          <IconAlertTriangle size={18} style={{ color: umbralAlert.critical ? 'var(--danger)' : 'var(--warning)', flexShrink: 0 }} />
          <span>
            <strong>{umbralAlert.critical ? 'Umbral excedido' : 'Cerca del umbral'}:</strong>{' '}
            {umbralAlert.horasUsadas}h / {umbralAlert.umbral}h ({umbralAlert.pct}%)
          </span>
        </div>
      )}

      {/* View toggle + Search */}
      <div className="proyecto-view-toggle">
        <button
          className={`tag-chip ${viewMode === 'info' ? 'active' : ''}`}
          onClick={() => setViewMode('info')}
        >
          <IconFileText size={14} /> Información
        </button>
        <button
          className={`tag-chip ${viewMode === 'kanban' ? 'active' : ''}`}
          onClick={() => setViewMode('kanban')}
        >
          <IconKanban size={14} /> Kanban
        </button>
        <button
          className={`tag-chip ${viewMode === 'list' ? 'active' : ''}`}
          onClick={() => setViewMode('list')}
        >
          <IconList size={14} /> Lista
        </button>
        <button
          className={`tag-chip ${viewMode === 'caja' ? 'active' : ''}`}
          onClick={() => setViewMode('caja')}
        >
          <IconDollar size={14} /> Caja
        </button>
        <button
          className={`tag-chip ${viewMode === 'reportes' ? 'active' : ''}`}
          onClick={() => setViewMode('reportes')}
        >
          <IconBarChart size={14} /> Reportes
        </button>
        <button
          className={`tag-chip ${viewMode === 'correos' ? 'active' : ''}`}
          onClick={() => setViewMode('correos')}
        >
          <IconMail size={14} /> Correos
        </button>
        <button
          className={`tag-chip ${viewMode === 'integraciones' ? 'active' : ''}`}
          onClick={() => setViewMode('integraciones')}
        >
          <IconPlug size={14} /> Integraciones
        </button>
        <div style={{ marginLeft: 'auto', position: 'relative', display: 'flex', alignItems: 'center' }}>
          <IconSearch size={14} style={{ position: 'absolute', left: 10, color: 'var(--text-muted)', pointerEvents: 'none' }} />
          <input
            type="text"
            className="form-input"
            placeholder="Buscar tarea..."
            value={searchQuery}
            onChange={e => { setSearchQuery(e.target.value); setListPage(0) }}
            style={{ paddingLeft: 30, fontSize: '0.82rem', width: 220, height: 32 }}
          />
          {searchQuery && (
            <button
              onClick={() => { setSearchQuery(''); setListPage(0) }}
              style={{ position: 'absolute', right: 8, background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', padding: 0, fontSize: '0.9rem', lineHeight: 1 }}
              title="Limpiar búsqueda"
            >
              ×
            </button>
          )}
        </div>
      </div>

      {/* Content */}
      {viewMode === 'info' ? (
        <div className="proyecto-info-view">
          {/* Grid de información */}
          <div className="proyecto-info-grid">
            {/* Columna izquierda — Detalles */}
            <div className="proyecto-info-main">
              {/* Descripción */}
              <div className="proyecto-info-card">
                <h3 className="proyecto-info-card-title">Descripción</h3>
                <p className="proyecto-info-card-text">
                  {proyecto.descripcion || <span style={{ color: 'var(--text-muted)', fontStyle: 'italic' }}>Sin descripción</span>}
                </p>
              </div>

              {/* Chats vinculados — normalmente el grupo de WhatsApp del cliente,
                  que es donde se pide el trabajo de este proyecto. */}
              {chatsVinculados.length > 0 && (
                <div className="proyecto-info-card">
                  <h3 className="proyecto-info-card-title">
                    <IconWhatsApp size={16} /> Chats vinculados
                    <span className="proyecto-info-badge">{chatsVinculados.length}</span>
                  </h3>
                  <div className="proyecto-chats-lista">
                    {chatsVinculados.map(chat => (
                      <button
                        key={chat.id}
                        className="proyecto-chat-item"
                        onClick={() => navigate(`/conversaciones/${chat.canal}/${chat.channel_id}/${chat.id}`)}
                        type="button"
                      >
                        <span className="proyecto-chat-item__nombre">
                          {(chat.metadata?.contact_name as string) || chat.chat_id}
                        </span>
                        <span className="proyecto-chat-item__ultimo">
                          {chat.ultimo_mensaje || 'Sin mensajes'}
                        </span>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Cotizaciones vinculadas */}
              <div className="proyecto-info-card">
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
                  <h3 className="proyecto-info-card-title" style={{ margin: 0 }}>
                    <IconFileText size={16} /> Cotizaciones Vinculadas
                    <span className="proyecto-info-badge">{proyectoCotizaciones.length}</span>
                  </h3>
                  <button
                    className="btn btn-sm btn-secondary"
                    onClick={() => { cargarCatalogos(); setShowAddCotizacion(!showAddCotizacion) }}
                    style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: '0.8rem' }}
                  >
                    <IconPlus size={13} /> Vincular
                  </button>
                </div>

                {/* Add cotizacion dropdown */}
                {showAddCotizacion && (
                  <div className="proyecto-info-add-cot">
                    <select
                      className="form-select"
                      defaultValue=""
                      onChange={e => {
                        if (e.target.value) handleAddCotizacion(Number(e.target.value))
                      }}
                      disabled={addingCotizacion}
                      style={{ fontSize: '0.85rem' }}
                    >
                      <option value="" disabled>— Seleccionar cotización —</option>
                      {availableCotizaciones.map(c => (
                        <option key={c.id} value={c.id}>#{c.numero} — {c.proyecto_nombre || c.cliente_nombre} ({formatCurrency(c.total, c.moneda)})</option>
                      ))}
                    </select>
                    {addingCotizacion && <div className="auth-spinner" style={{ width: 16, height: 16 }} />}
                  </div>
                )}

                {proyectoCotizaciones.length === 0 ? (
                  <p style={{ color: 'var(--text-muted)', fontSize: '0.85rem', fontStyle: 'italic' }}>
                    No hay cotizaciones vinculadas a este proyecto.
                  </p>
                ) : (
                  <div className="proyecto-cot-list">
                    {proyectoCotizaciones.map(pc => (
                      <div key={pc.id} className="proyecto-cot-item">
                        <div className="proyecto-cot-item-info">
                          <button
                            type="button"
                            className="proyecto-cot-link"
                            onClick={() => setVisorCotizacion({ id: pc.cotizacion_id, numero: pc.numero })}
                            title="Ver la cotizacion sin salir del proyecto"
                          >
                            <IconFileText size={14} />
                            <strong>#{pc.numero}</strong>
                            <span className="proyecto-cot-name">{pc.proyecto_nombre || pc.cliente_nombre}</span>
                          </button>
                          <div className="proyecto-cot-meta">
                            {pc.total != null && (
                              <span className="proyecto-cot-amount">{formatCurrency(pc.total, pc.moneda || proyecto.moneda)}</span>
                            )}
                            {pc.estado && (
                              <span className={`badge badge-cot-${pc.estado}`}>{pc.estado}</span>
                            )}
                          </div>
                        </div>
                        <button
                          className="btn btn-icon btn-sm btn-secondary"
                          onClick={() => handleRemoveCotizacion(pc.cotizacion_id)}
                          title="Desvincular cotización"
                          style={{ color: 'var(--danger)', flexShrink: 0 }}
                        >
                          <IconTrash size={13} />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Hitos de cobro: salen del cronograma de la cotización vinculada */}
              <HitosProyecto proyectoId={proyecto.id} moneda={proyecto.moneda} modeloCobro={proyecto.modelo_cobro} />
              <ReunionesProyecto proyectoId={proyecto.id} />

              {/* Notas internas */}
              {proyecto.notas && (
                <div className="proyecto-info-card">
                  <h3 className="proyecto-info-card-title">📝 Notas Internas</h3>
                  <p className="proyecto-info-card-text" style={{ whiteSpace: 'pre-wrap' }}>{proyecto.notas}</p>
                </div>
              )}
            </div>

            {/* Columna derecha — Sidebar */}
            <div className="proyecto-info-sidebar">
              {/* Datos generales */}
              <div className="proyecto-info-card">
                <h3 className="proyecto-info-card-title">Datos del Proyecto</h3>
                <div className="proyecto-info-data-list">
                  <div className="proyecto-info-data-row">
                    <span className="proyecto-info-data-label"><IconUser size={13} /> Cliente</span>
                    <span className="proyecto-info-data-value">{clienteName}</span>
                  </div>
                  {servicioName && (
                    <div className="proyecto-info-data-row">
                      <span className="proyecto-info-data-label"><IconPackage size={13} /> Servicio</span>
                      <span className="proyecto-info-data-value">{servicioName}</span>
                    </div>
                  )}
                  <div className="proyecto-info-data-row">
                    <span className="proyecto-info-data-label"><IconDollar size={13} /> Presupuesto</span>
                    <span className="proyecto-info-data-value" style={{ fontWeight: 600, color: 'var(--accent-primary)' }}>
                      {proyecto.presupuesto > 0 ? formatCurrency(proyecto.presupuesto, proyecto.moneda) : '—'}
                    </span>
                  </div>
                  <div className="proyecto-info-data-row">
                    <span className="proyecto-info-data-label">💱 Moneda</span>
                    <span className="proyecto-info-data-value">{proyecto.moneda}</span>
                  </div>
                </div>
              </div>

              {/* Fechas */}
              <div className="proyecto-info-card">
                <h3 className="proyecto-info-card-title"><IconCalendar size={14} /> Fechas</h3>
                <div className="proyecto-info-data-list">
                  <div className="proyecto-info-data-row">
                    <span className="proyecto-info-data-label">Inicio</span>
                    <span className="proyecto-info-data-value">{proyecto.fecha_inicio ? formatDate(proyecto.fecha_inicio) : '—'}</span>
                  </div>
                  <div className="proyecto-info-data-row">
                    <span className="proyecto-info-data-label">Fin estimado</span>
                    <span className="proyecto-info-data-value">{proyecto.fecha_fin_estimada ? formatDate(proyecto.fecha_fin_estimada) : '—'}</span>
                  </div>
                  <div className="proyecto-info-data-row">
                    <span className="proyecto-info-data-label">Creado</span>
                    <span className="proyecto-info-data-value">{formatDate(proyecto.created_at.split('T')[0])}</span>
                  </div>
                </div>
              </div>

              {/* GitHub */}
              {proyecto.github_repo && (
                <div className="proyecto-info-card">
                  <h3 className="proyecto-info-card-title">🔗 Repositorio</h3>
                  <a
                    href={`https://github.com/${proyecto.github_repo}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="proyecto-info-github-link"
                  >
                    <IconExternalLink size={13} /> {proyecto.github_repo}
                  </a>
                </div>
              )}

              {/* Resumen de tareas */}
              <div className="proyecto-info-card">
                <h3 className="proyecto-info-card-title">📊 Resumen</h3>
                <div className="proyecto-info-data-list">
                  <div className="proyecto-info-data-row">
                    <span className="proyecto-info-data-label">Tareas</span>
                    <span className="proyecto-info-data-value">{stats.total}</span>
                  </div>
                  <div className="proyecto-info-data-row">
                    <span className="proyecto-info-data-label">Completadas</span>
                    <span className="proyecto-info-data-value" style={{ color: 'var(--success)' }}>{stats.done}</span>
                  </div>
                  <div className="proyecto-info-data-row">
                    <span className="proyecto-info-data-label">Progreso</span>
                    <span className="proyecto-info-data-value">{stats.progress}%</span>
                  </div>
                  {stats.overdue > 0 && (
                    <div className="proyecto-info-data-row">
                      <span className="proyecto-info-data-label">Vencidas</span>
                      <span className="proyecto-info-data-value" style={{ color: 'var(--danger)' }}>{stats.overdue}</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      ) : (viewMode === 'kanban' || viewMode === 'list') && tareas.length === 0 ? (
        /* El vacío de tareas solo aplica a las vistas que listan tareas: antes se
           comía también Caja, Reportes y Correos en proyectos sin tareas. */
        <div className="empty-state">
          <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
            <rect x="3" y="3" width="18" height="18" rx="2"/>
            <path d="M9 12l2 2 4-4"/>
          </svg>
          <h3>Sin tareas</h3>
          <p>Agrega la primera tarea para este proyecto</p>
          <button className="btn btn-primary" onClick={openCreateTarea}>+ Nueva Tarea</button>
        </div>
      ) : viewMode === 'kanban' ? (
        <>
        {barraFiltros}
        <KanbanBoard
          tareas={filteredTareas}
          estadosVisibles={filterEstado}
          evidencias={evidencias}
          proyectoId={proyecto.id}
          onTareaClick={openEditTarea}
          onRefresh={loadData}
          onApprove={handleQuickApprove}
        />
        </>
      ) : viewMode === 'caja' ? (
        <CajaProyecto
          proyecto={proyecto}
          tareas={searchedTareas}
          facturas={facturas}
          cotizaciones={cotizacionesVinculadas}
          tarifaCliente={tarifaCliente}
          onGenerarFactura={(ids) => { setPreSelectedTaskIds(ids); setShowFacturarModal(true) }}
          onRefresh={loadData}
        />
      ) : viewMode === 'reportes' ? (
        <ReportesProyecto
          proyecto={proyecto}
        />
      ) : viewMode === 'correos' ? (
        <PanelCorreos basePath={`/api/proyectos/${proyecto.id}/correos`} nombreFicha="proyecto" />
      ) : viewMode === 'integraciones' ? (
        <IntegracionesProyecto proyectoId={proyecto.id} proyectoNombre={proyecto.nombre} />
      ) : (
        /* List view */
        <>
          {/* Bulk action bar - outside scroll */}
          {listSelected.size > 0 && (
            <div style={{
              display: 'flex', alignItems: 'center', gap: 12, padding: '10px 16px',
              background: 'color-mix(in srgb, var(--primary-light) 10%, transparent)', borderRadius: 8, marginBottom: 12,
              border: '1px solid color-mix(in srgb, var(--primary-light) 25%, transparent)', flexWrap: 'wrap',
            }}>
              <span style={{ fontSize: '0.85rem', fontWeight: 600, color: 'var(--accent-primary)', minWidth: 'fit-content' }}>
                {listSelected.size} seleccionada{listSelected.size > 1 ? 's' : ''}
              </span>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <label style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', whiteSpace: 'nowrap' }}>Estado:</label>
                <select
                  className="form-input"
                  style={{ padding: '4px 8px', fontSize: '0.82rem', width: 'auto', minWidth: 130 }}
                  defaultValue=""
                  onChange={e => { if (e.target.value) { bulkUpdate('estado', e.target.value); e.target.value = '' } }}
                  disabled={bulkSaving}
                >
                  <option value="" disabled>Cambiar a...</option>
                  <option value="pendiente">Pendiente</option>
                  <option value="en_progreso">En Progreso</option>
                  <option value="completada">Completada</option>
                  <option value="bloqueada">Bloqueada</option>
                  <option value="cancelada">Cancelada</option>
                </select>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <label style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', whiteSpace: 'nowrap' }}>Prioridad:</label>
                <select
                  className="form-input"
                  style={{ padding: '4px 8px', fontSize: '0.82rem', width: 'auto', minWidth: 120 }}
                  defaultValue=""
                  onChange={e => { if (e.target.value) { bulkUpdate('prioridad', e.target.value); e.target.value = '' } }}
                  disabled={bulkSaving}
                >
                  <option value="" disabled>Cambiar a...</option>
                  <option value="baja">Baja</option>
                  <option value="media">Media</option>
                  <option value="alta">Alta</option>
                  <option value="urgente">Urgente</option>
                </select>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <label style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', whiteSpace: 'nowrap' }}>Aprobación:</label>
                <select
                  className="form-input"
                  style={{ padding: '4px 8px', fontSize: '0.82rem', width: 'auto', minWidth: 120 }}
                  defaultValue=""
                  onChange={e => { if (e.target.value) { bulkUpdate('estado_aprobacion', e.target.value); e.target.value = '' } }}
                  disabled={bulkSaving}
                >
                  <option value="" disabled>Cambiar a...</option>
                  <option value="pendiente">Pendiente</option>
                  <option value="enviada">Enviada</option>
                  <option value="aprobada">Aprobada</option>
                  <option value="rechazada">Rechazada</option>
                </select>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <label style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', whiteSpace: 'nowrap' }}>Fecha Límite:</label>
                <input
                  type="date"
                  className="form-input"
                  style={{ padding: '4px 8px', fontSize: '0.82rem', width: 'auto' }}
                  onChange={e => { if (e.target.value) bulkUpdate('fecha_limite', e.target.value) }}
                  disabled={bulkSaving}
                />
              </div>
              <button
                className="btn btn-sm btn-secondary"
                onClick={() => setListSelected(new Set())}
                style={{ marginLeft: 'auto', fontSize: '0.78rem' }}
              >
                Deseleccionar
              </button>
              {bulkSaving && <div className="auth-spinner" style={{ width: 16, height: 16 }} />}
            </div>
          )}

          {barraFiltros}

          {/* Scrollable table */}
          <div className="table-container">
          <table>
            <thead>
              <tr>
                <th style={{ width: 36 }}>
                  <input
                    type="checkbox"
                    checked={listSelected.size === filteredTareas.length && filteredTareas.length > 0}
                    onChange={toggleListSelectAll}
                  />
                </th>
                <th>Título</th>
                <th>Estado</th>
                <th>Prioridad</th>
                <th>Monto</th>
                <th>Aprobación</th>
                <th title="Fecha de Inicio">FI</th>
                <th title="Fecha Límite">FL</th>
                <th>Etiquetas</th>
                <th>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {agruparHitos && hayHitos ? (
                <>
                  {gruposHito.grupos.map(({ hito, hijas }) => {
                    const abierto = hitosAbiertos.has(hito.id)
                    const listas = hijas.filter(h => h.estado === 'completada').length
                    const pct = hijas.length ? Math.round((listas / hijas.length) * 100) : 0
                    return (
                      <Fragment key={`hito-${hito.id}`}>
                        <tr className="fila-hito">
                          <td colSpan={10}>
                            <div className="hito-cabecera">
                              <button
                                className="hito-toggle"
                                onClick={() => toggleHito(hito.id)}
                                title={abierto ? 'Contraer' : 'Desplegar'}
                              >
                                {abierto ? <IconChevronDown size={14} /> : <IconChevronRight size={14} />}
                              </button>
                              <span className="hito-titulo">{hito.titulo}</span>
                              <span className="hito-avance">{listas}/{hijas.length}</span>
                              <span className="hito-barra"><i style={{ width: `${pct}%` }} /></span>
                              {hito.descripcion && (
                                <button
                                  className="btn btn-sm btn-secondary"
                                  onClick={() => setHitoDetalle(hitoDetalle === hito.id ? null : hito.id)}
                                >
                                  {hitoDetalle === hito.id ? 'Ocultar' : 'Qué incluye'}
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                        {hitoDetalle === hito.id && hito.descripcion && (
                          <tr className="fila-hito-detalle">
                            <td colSpan={10}>
                              {/* Texto de la cotizacion: lo que se le vendio al
                                  cliente, tal cual. Se respeta el salto de linea
                                  porque son listas de entregables. */}
                              <div className="hito-detalle">{hito.descripcion}</div>
                            </td>
                          </tr>
                        )}
                        {abierto && hijas.map(filaTarea)}
                      </Fragment>
                    )
                  })}
                  {gruposHito.sueltas.length > 0 && (
                    <>
                      <tr className="fila-hito">
                        <td colSpan={10}>
                          <div className="hito-cabecera">
                            <span className="hito-titulo" style={{ color: 'var(--text-muted)' }}>
                              Sin hito
                            </span>
                            <span className="hito-avance">{gruposHito.sueltas.length}</span>
                          </div>
                        </td>
                      </tr>
                      {gruposHito.sueltas.map(filaTarea)}
                    </>
                  )}
                </>
              ) : (
                paginatedTareas.map(filaTarea)
              )}
            </tbody>
          </table>
          </div>

          {/* Paginacion. NO se muestra al agrupar: ahi el colapso ya acota el
              alto, y paginar partiria un hito entre dos paginas. */}
          {!(agruparHitos && hayHitos) && totalPages > 1 && (
            <div style={{
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
              padding: '10px 0 0', flexShrink: 0,
              borderTop: '1px solid var(--border)',
            }}>
              <button
                className="btn btn-sm btn-secondary"
                onClick={() => setListPage(p => Math.max(0, p - 1))}
                disabled={listPage === 0}
                style={{ fontSize: '0.78rem', padding: '4px 10px' }}
              >
                ← Anterior
              </button>
              {Array.from({ length: totalPages }, (_, i) => (
                <button
                  key={i}
                  className={`btn btn-sm ${i === listPage ? 'btn-primary' : 'btn-secondary'}`}
                  onClick={() => setListPage(i)}
                  style={{ fontSize: '0.78rem', padding: '4px 10px', minWidth: 32 }}
                >
                  {i + 1}
                </button>
              ))}
              <button
                className="btn btn-sm btn-secondary"
                onClick={() => setListPage(p => Math.min(totalPages - 1, p + 1))}
                disabled={listPage >= totalPages - 1}
                style={{ fontSize: '0.78rem', padding: '4px 10px' }}
              >
                Siguiente →
              </button>
            </div>
          )}
        </>
      )}

      {/* Tarea Modal */}
      {showTareaModal && (
        <TareaModal
          tarea={editTarea}
          proyectoId={proyecto.id}
          clienteId={proyecto.cliente_id}
          tarifaCliente={tarifaCliente}
          facturas={facturas}
          onClose={() => setShowTareaModal(false)}
          onSaved={() => { setShowTareaModal(false); loadData() }}
          onDeleted={() => { setShowTareaModal(false); loadData() }}
        />
      )}

      {visorCotizacion && (
        <CotizacionVisorModal
          cotizacionId={visorCotizacion.id}
          numero={visorCotizacion.numero}
          onClose={() => setVisorCotizacion(null)}
        />
      )}

      {/* Proyecto Edit Modal */}
      {showProyectoModal && (
        <ProyectoModal
          proyecto={proyecto}
          clientes={clientes}
          servicios={servicios}
          cotizaciones={cotizaciones}
          onClose={() => setShowProyectoModal(false)}
          onSaved={() => { setShowProyectoModal(false); loadData() }}
        />
      )}

      {/* Facturar Tareas Modal */}
      {showFacturarModal && (
        <FacturarTareasModal
          proyectoId={proyecto.id}
          proyectoNombre={proyecto.nombre}
          cliente={cliente}
          tareas={tareas}
          facturas={facturas}
          tarifaCliente={tarifaCliente}
          moneda={proyecto.moneda}
          preSelectedIds={preSelectedTaskIds}
          proyectoCotizaciones={proyectoCotizaciones}
          onClose={() => setShowFacturarModal(false)}
          onSaved={() => { setShowFacturarModal(false); loadData() }}
        />
      )}
      {/* Importar Tareas Modal */}
      {showImportModal && proyecto && (
        <ImportarTareasModal
          proyectoId={proyecto.id}
          proyectoNombre={proyecto.nombre}
          onClose={() => setShowImportModal(false)}
          onImported={() => { setShowImportModal(false); loadData() }}
        />
      )}
    </div>
  )
}
